/**
 * Lightbox Component
 * A lightweight, accessible lightbox for image viewing
 */

import type { LightboxImage } from '../types';

export class Lightbox {
  private images: LightboxImage[] = [];
  private currentIndex = 0;
  private lightboxEl: HTMLElement | null = null;
  private isOpen = false;

  private imageEl!: HTMLImageElement;
  private captionEl!: HTMLElement;
  private counterEl!: HTMLElement;
  private closeBtn!: HTMLButtonElement;
  private prevBtn!: HTMLButtonElement;
  private nextBtn!: HTMLButtonElement;

  constructor() {
    this.init();
  }

  private init(): void {
    this.createLightbox();
    this.bindImages();
    this.bindKeyboard();
  }

  private createLightbox(): void {
    this.lightboxEl = document.createElement('div');
    this.lightboxEl.className = 'lightbox';
    this.lightboxEl.setAttribute('role', 'dialog');
    this.lightboxEl.setAttribute('aria-modal', 'true');
    this.lightboxEl.setAttribute('aria-label', 'Bildansicht');

    this.lightboxEl.innerHTML = `
      <div class="lightbox__content">
        <button class="lightbox__close" aria-label="Schließen">
          <svg viewBox="0 0 58 58" aria-hidden="true">
            <path d="M35.8095 28.9647C35.8095 28.9647 35.7994 28.9547 35.7894 28.9446C36.8625 28.0276 37.9556 27.1005 39.0688 26.1633C40.9943 24.551 42.8897 22.979 44.755 21.4574C46.6304 19.9257 48.3653 18.5452 49.9699 17.3058L44.9556 10C44.4542 10.2318 43.712 10.7255 42.7192 11.4612C41.7364 12.1968 40.6032 13.0936 39.3295 14.1315C38.0559 15.1695 36.7622 16.2578 35.4384 17.3864C34.1146 18.515 32.861 19.5831 31.6777 20.611C30.4943 21.6388 29.5115 22.5155 28.7393 23.2511C27.937 22.4752 26.9341 21.5381 25.7307 20.4296C24.5272 19.3211 23.2235 18.1522 21.8195 16.9329C20.4155 15.7136 19.0115 14.5145 17.6074 13.3556C16.2034 12.1968 14.8997 11.1689 13.6963 10.2519L8 16.9934C10.1361 18.8778 12.4427 20.8226 14.9198 22.838C17.3968 24.8534 19.8138 26.9191 22.1705 29.0353C22.1705 29.0353 22.1805 29.0453 22.1905 29.0554C21.1175 29.9724 20.0244 30.8995 18.9112 31.8366C16.9857 33.449 15.0903 35.0209 13.2249 36.5426C11.3496 38.0742 9.61461 39.4548 8.01003 40.6942L13.0244 48C13.5258 47.7682 14.2679 47.2745 15.2607 46.5388C16.2436 45.8032 17.3768 44.9064 18.6504 43.8685C19.9241 42.8305 21.2178 41.7422 22.5415 40.6136C23.8653 39.485 25.1189 38.4169 26.3023 37.389C27.4857 36.3612 28.4685 35.4845 29.2407 34.7489C30.043 35.5248 31.0458 36.4619 32.2493 37.5704C33.4527 38.6789 34.7564 39.8478 36.1605 41.0671C37.5645 42.2864 38.9685 43.4855 40.3725 44.6444C41.7765 45.8032 43.0802 46.8311 44.2837 47.7481L50 40.9966C47.8639 39.1122 45.5573 37.1673 43.0802 35.1519C40.6032 33.1366 38.1862 31.0708 35.8295 28.9547L35.8095 28.9647Z" fill="white"/>
          </svg>
        </button>
        <button class="lightbox__nav lightbox__nav--prev" aria-label="Vorheriges Bild">
          <svg viewBox="0 0 58 58" aria-hidden="true">
            <path d="M35.0914 8L41.7514 13.7204C40.8568 14.9247 39.8429 16.2294 38.6898 17.6344C37.5467 19.0394 36.3737 20.4444 35.161 21.8495C33.9483 23.2545 32.7952 24.5591 31.7117 25.7634C30.6182 26.9677 29.6938 27.9713 28.9284 28.7742C29.654 29.547 30.5288 30.5204 31.5327 31.7147C32.5467 32.8989 33.6003 34.1534 34.7137 35.4781C35.827 36.8029 36.8906 38.0975 37.9244 39.372C38.9482 40.6466 39.8329 41.7706 40.5586 42.7642C41.2842 43.7477 41.7713 44.5004 41.9999 45.0022L34.7932 50.0201C33.5705 48.4143 32.1987 46.6781 30.6978 44.8014C29.1868 42.9247 27.6361 41.028 26.0556 39.1111C24.4651 37.1842 22.8946 35.3176 21.3339 33.491C19.7733 31.6645 18.332 29.9986 17.0099 28.4932C19.0675 26.6566 21.1351 24.5491 23.2226 22.2007C25.3101 19.8423 27.3479 17.4237 29.3359 14.9448C31.324 12.466 33.2425 10.1577 35.1013 8.02007L35.0914 8Z" fill="white"/>
          </svg>
        </button>
        <img class="lightbox__image" src="" alt="">
        <button class="lightbox__nav lightbox__nav--next" aria-label="Nächstes Bild">
          <svg viewBox="0 0 58 58" aria-hidden="true">
            <path d="M23.9085 50L17.2485 44.2796C18.1431 43.0753 19.1571 41.7706 20.3101 40.3656C21.4533 38.9606 22.6262 37.5556 23.839 36.1505C25.0517 34.7455 26.2048 33.4409 27.2883 32.2366C28.3817 31.0323 29.3062 30.0287 30.0716 29.2258C29.3459 28.453 28.4712 27.4796 27.4672 26.2853C26.4533 25.1011 25.3996 23.8466 24.2863 22.5219C23.173 21.1971 22.1093 19.9025 21.0755 18.628C20.0517 17.3534 19.167 16.2294 18.4414 15.2358C17.7157 14.2523 17.2286 13.4996 17 12.9978L24.2068 7.97992C25.4294 9.58566 26.8012 11.3219 28.3022 13.1986C29.8131 15.0753 31.3638 16.972 32.9443 18.8889C34.5348 20.8158 36.1054 22.6824 37.666 24.509C39.2266 26.3355 40.668 28.0014 41.9901 29.5068C39.9324 31.3434 37.8648 33.4509 35.7773 35.7993C33.6899 38.1577 31.6521 40.5763 29.664 43.0552C27.6759 45.5341 25.7575 47.8423 23.8986 49.9799L23.9085 50Z" fill="white"/>
          </svg>
        </button>
        <p class="lightbox__caption"></p>
        <p class="lightbox__counter"></p>
      </div>
    `;

    document.body.appendChild(this.lightboxEl);

    // Cache elements
    this.imageEl = this.lightboxEl.querySelector('.lightbox__image')!;
    this.captionEl = this.lightboxEl.querySelector('.lightbox__caption')!;
    this.counterEl = this.lightboxEl.querySelector('.lightbox__counter')!;
    this.closeBtn = this.lightboxEl.querySelector('.lightbox__close')!;
    this.prevBtn = this.lightboxEl.querySelector('.lightbox__nav--prev')!;
    this.nextBtn = this.lightboxEl.querySelector('.lightbox__nav--next')!;

    // Bind button events
    this.closeBtn.addEventListener('click', () => this.close());
    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());

    // Close on background click
    this.lightboxEl.addEventListener('click', (e: MouseEvent) => {
      if (e.target === this.lightboxEl) {
        this.close();
      }
    });

    // Close on image click
    this.imageEl.addEventListener('click', () => this.close());

    // Close on content click (but not on nav buttons)
    const content = this.lightboxEl.querySelector('.lightbox__content');
    if (content) {
      content.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.lightbox__nav') && !target.closest('.lightbox__close')) {
          this.close();
        }
      });
    }
  }

  private bindImages(): void {
    const imageWrappers = document.querySelectorAll<HTMLElement>(
      '.stolperstein-info__image-wrapper, .stolperstein-embed__image-wrapper, .gedenkort-gallery .splide__slide'
    );

    imageWrappers.forEach((wrapper, index) => {
      const img = wrapper.querySelector<HTMLImageElement>('img');
      const caption = wrapper.querySelector('figcaption');

      if (!img) return;

      wrapper.classList.add('stolperstein-info__image-wrapper--clickable');
      wrapper.setAttribute('tabindex', '0');
      wrapper.setAttribute('role', 'button');
      wrapper.setAttribute('aria-label', `Bild vergrößern: ${img.alt || 'Bild'}`);

      const group = (wrapper.closest('[data-lightbox-group]') as HTMLElement | null)?.dataset.lightboxGroup || 'default';
      this.images.push({
        src: img.dataset.full || img.src,
        alt: img.alt || '',
        caption: caption ? caption.textContent || '' : img.alt || '',
        group
      });

      wrapper.addEventListener('click', () => {
        this.open(index);
      });

      wrapper.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open(index);
        }
      });
    });

    this.updateNavVisibility();
  }

  private updateNavVisibility(): void {
    if (this.images.length <= 1) {
      this.prevBtn.style.display = 'none';
      this.nextBtn.style.display = 'none';
      this.counterEl.style.display = 'none';
    } else {
      this.prevBtn.style.display = 'flex';
      this.nextBtn.style.display = 'flex';
      this.counterEl.style.display = 'block';
    }
  }

  private bindKeyboard(): void {
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
      }
    });
  }

  public open(index: number): void {
    this.currentIndex = index;
    this.updateImage();
    this.lightboxEl?.classList.add('is-open');
    this.isOpen = true;

    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      this.closeBtn.focus();
    }, 100);

    this.lightboxEl?.setAttribute('aria-hidden', 'false');
  }

  public close(): void {
    this.lightboxEl?.classList.remove('is-open');
    this.isOpen = false;

    document.body.style.overflow = '';

    const clickedWrapper = document.querySelectorAll<HTMLElement>(
      '.stolperstein-info__image-wrapper--clickable'
    )[this.currentIndex];
    if (clickedWrapper) {
      clickedWrapper.focus();
    }

    this.lightboxEl?.setAttribute('aria-hidden', 'true');
  }

  private groupIndices(): number[] {
    const group = this.images[this.currentIndex]?.group || 'default';
    const indices: number[] = [];
    this.images.forEach((img, i) => {
      if ((img.group || 'default') === group) indices.push(i);
    });
    return indices;
  }

  public prev(): void {
    const g = this.groupIndices();
    if (g.length <= 1) return;
    const pos = g.indexOf(this.currentIndex);
    this.currentIndex = g[(pos - 1 + g.length) % g.length];
    this.updateImage();
  }

  public next(): void {
    const g = this.groupIndices();
    if (g.length <= 1) return;
    const pos = g.indexOf(this.currentIndex);
    this.currentIndex = g[(pos + 1) % g.length];
    this.updateImage();
  }

  private updateImage(): void {
    const image = this.images[this.currentIndex];
    if (!image) return;

    this.imageEl.style.opacity = '0';
    this.imageEl.src = image.src;
    this.imageEl.alt = image.alt;
    this.captionEl.textContent = image.caption;

    const g = this.groupIndices();
    const pos = g.indexOf(this.currentIndex);
    if (g.length > 1) {
      this.counterEl.textContent = `${pos + 1} / ${g.length}`;
      this.counterEl.style.display = 'block';
      this.prevBtn.style.display = 'flex';
      this.nextBtn.style.display = 'flex';
    } else {
      this.counterEl.style.display = 'none';
      this.prevBtn.style.display = 'none';
      this.nextBtn.style.display = 'none';
    }

    this.imageEl.onload = () => {
      this.imageEl.style.opacity = '1';
    };

    if (this.imageEl.complete) {
      this.imageEl.style.opacity = '1';
    }
  }
}

/**
 * Auto-initialize lightbox on pages with images
 */
export function initLightbox(): Lightbox | null {
  const hasImages = document.querySelector(
    '.stolperstein-info__image-wrapper, .stolperstein-embed__image-wrapper, .gedenkort-gallery .splide__slide'
  );

  if (hasImages) {
    return new Lightbox();
  }
  return null;
}
